import { useQuery, useQueryClient } from '@tanstack/react-query';
import useApi from '../../../../../../../utils/AxiosWrapper';
import { useDemo } from '../../../../../../../utils/DemoContext';
import { useUser } from '../../../../../../../utils/UserContext';
import { useFleetInsurance } from '../fleet-insurance/useFleetInsurance';

export interface PendingTruck {
  truck_id: string;
  customer_id: string;
  vehicle_id_number: string | null;
  truck_display_name: string;
  truck_make: string;
  truck_model: string | null;
  truck_year: number;
  ownership_status: string;
  starting_mileage: number;
  current_mileage: number;
  typical_annual_mileage: number | null;
  truck_class_number: number | null;
  truck_payment_amount: number;
  insurance_payment_amount: number;
  eld_id: string | null;
  eld_type: string | null;
  external_eld_vehicle_id: string | null;
  status: string;
  created_at: string;
}

export const usePendingTrucks = () => {
  const { userId } = useUser();
  const { isDemo } = useDemo();
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();
  // THT-675 — approval collects an insurance amount, so the card needs to know
  // whether the customer is on a fleet-wide policy. Sourced here so
  // PendingTruckApprovals stays a plain renderer over this hook's output.
  const { isFleetMode: isFleetInsurance } = useFleetInsurance();

  const queryKey = ['pendingTrucks', userId ?? ''];

  const fetchPendingTrucks = async (): Promise<PendingTruck[]> => {
    const response = await callProtectedApi(
      `/trucks/pending/${userId ?? ''}`,
      'get',
      {}
    );
    return response?.data?.data?.trucks || [];
  };

  // The demo session carries a fake userId and token, so `!!userId` alone let
  // GET /trucks/pending fire and 4xx, surfacing the error banner on the demo
  // Assets tab. Sitting out the demo mirrors the sibling assets query
  // (AssetsHome.tsx). Real pre-active customers are deliberately not gated —
  // the endpoint authorizes them (customer_must_be_active: false).
  const query = useQuery({
    queryKey,
    queryFn: fetchPendingTrucks,
    enabled: !!userId && !isDemo,
    staleTime: 30_000,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey });
    // Approving promotes a pending truck to active, so the assets list for
    // this user must refetch. Scoped to userId so other customers' caches
    // (e.g. admin-impersonation views) are not invalidated.
    queryClient.invalidateQueries({ queryKey: ['assets', userId ?? ''] });
  };

  const approve = async (truck_id: string, body: Record<string, any>) => {
    await callProtectedApi(`/trucks/${truck_id}/approve`, 'patch', body);
    refresh();
  };

  const reject = async (truck_id: string) => {
    await callProtectedApi(`/trucks/${truck_id}/reject`, 'patch', {});
    refresh();
  };

  return {
    pendingTrucks: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    isFleetInsurance,
    refresh,
    approve,
    reject,
  };
};
